/**
 * Canonical Streaming Dashboard link builders.
 *
 * Every deep link into the Control Center — a server user, a media item, a
 * device search, the TV Guide with restored filters — is built here so callers
 * never hand-roll a path or forget to encode a raw id. Route ids and names go in
 * raw; the helper encodes them. Pure and DOM-free so the shapes are unit-testable
 * in isolation.
 */
import { TV_GUIDE_PATH } from "./mediaBackLink.js";

const CONTROL_CENTER = "/streaming/dashboard/control-center";

/**
 * The inner tabs a server user's detail page exposes, in render order. The
 * source of truth for both the `?tab=` query the page reads and the deep links
 * that target a specific tab — modelled on {@link ../profileTabs}.
 */
export const serverUserTabs = ["overview", "controls", "devices", "history", "templates"] as const;

export type ServerUserTab = (typeof serverUserTabs)[number];

export const DEFAULT_SERVER_USER_TAB: ServerUserTab = "overview";

/** Resolve the active server-user tab from a URL search string; falls back to overview. */
export const parseServerUserTab = (search: string): ServerUserTab => {
  const value = new URLSearchParams(search.startsWith("?") ? search : `?${search}`).get("tab");
  const lowered = (value ?? "").toLowerCase();
  return serverUserTabs.includes(lowered as ServerUserTab)
    ? (lowered as ServerUserTab)
    : DEFAULT_SERVER_USER_TAB;
};

/**
 * The one class for a primary entity `Link` — a server user, media item,
 * channel, programme or provider name. Theme-aware by construction: base
 * `text-foreground`, hover shifts to the theme accent `text-primary`, never a
 * hardcoded blue. Callers add layout only (truncate, sizing, weight).
 */
export const entityLinkClass =
  "text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70 rounded-sm";

/**
 * As {@link entityLinkClass}, for a secondary/detail `Link` that sits in muted
 * copy (a crumb, a device row's username, a meta-row jump) — base
 * `text-muted-foreground`, same theme-accent hover.
 */
export const entityDetailLinkClass =
  "text-muted-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70 rounded-sm";

/**
 * A server user's detail page. `userId` is the stable key; when it is absent the
 * name is the fallback anchor (the same `userId || userName` order the session
 * rows use).
 */
export const serverUserPath = (
  serverType: string,
  userId: string | null | undefined,
  userName: string,
  options?: { tab?: ServerUserTab },
): string => {
  const base = `${CONTROL_CENTER}/server-users/${serverType}/${encodeURIComponent(userId || userName)}`;
  if (!options?.tab || options.tab === DEFAULT_SERVER_USER_TAB) return base;
  return `${base}?tab=${options.tab}`;
};

/** A media item's detail page in the Control Center. */
export const mediaItemPath = (serverType: string, itemId: string): string =>
  `${CONTROL_CENTER}/media/item/${serverType}/${encodeURIComponent(itemId)}`;

/**
 * A device's canonical detail page, keyed by (serverType, deviceId) across every
 * username that has used it. `serverType` is a raw segment; the device id is
 * encoded because it is an upstream-controlled value.
 */
export const devicePath = (serverType: string, deviceId: string): string =>
  `${CONTROL_CENTER}/devices/${serverType}/${encodeURIComponent(deviceId)}`;

/** A library's detail page in the Media Portal (same route `MediaLibraryDetailPage` serves). */
export const libraryPath = (serverType: string, libraryId: string): string =>
  `${CONTROL_CENTER}/media/library/${serverType}/${encodeURIComponent(libraryId)}`;

/**
 * The canonical detail route for a media request, shared with the media-requests
 * canvas. Kept here so every deep-link builder lives in one module;
 * `lib/mediaRequests/canvas` re-exports it so its own callers are untouched. The
 * param is structural (not the full `MediaRequest`) to keep this module free of
 * request-domain coupling. Every segment is encoded: a requester-controlled id
 * or an instance owner's name can contain path-reserved characters.
 */
export const requestDetailPath = (request: {
  providerId: string;
  ownerUsername: string;
  mediaType: string;
  requestId: string;
}): string =>
  [
    `${CONTROL_CENTER}/requests`,
    encodeURIComponent(request.providerId),
    encodeURIComponent(request.ownerUsername),
    encodeURIComponent(request.mediaType),
    encodeURIComponent(request.requestId),
  ].join("/");

/** The devices list pre-filtered to one device name via its search box. */
export const deviceSearchPath = (deviceName: string): string =>
  `${CONTROL_CENTER}/devices?search=${encodeURIComponent(deviceName)}`;

/**
 * The TV Guide, optionally restoring provider/group filters as query params (the
 * same param names and order the Media Portal back link uses). A falsy filter is
 * omitted so the "all" default carries no param.
 */
export const tvGuidePath = (filters?: { provider?: string | null; group?: string | null }): string => {
  const search = new URLSearchParams();
  if (filters?.provider) search.set("provider", filters.provider);
  if (filters?.group) search.set("group", filters.group);
  const query = search.toString();
  return query ? `${TV_GUIDE_PATH}?${query}` : TV_GUIDE_PATH;
};
